import React, { useEffect, useState } from 'react';
import { Box, HStack, Text, VStack, Spinner, Badge } from '@chakra-ui/react';
import { X, Download, History } from 'lucide-react';
import axios from 'axios';
import dayjs from 'dayjs';
import { baseUrl } from '../../../../hooks/useEndpoints';
import { ISession } from '../../../../models/sessions';
import CustomButton from '../../../../shared/CustomButton/CustomButton';
import { btnInline } from '../../theme/leoTokens';
import { ImportBadge } from '../m6-2/ImportBadge';
import { ReassignmentHistoryModal } from '../m6-2/ReassignmentHistoryModal';

interface DayDetailModalProps {
  open: boolean;
  onClose: () => void;
  date: string;
  athleteId?: string;
  athleteName?: string;
  onImportClick: () => void;
}

/**
 * Modale Dettaglio Giorno (Addendum v8 EP3 — header action "+ Importa dati ▾").
 * Mostra sessioni esistenti del (date, atleta) + CTA Importa pre-popolata.
 */
export const DayDetailModal: React.FC<DayDetailModalProps> = ({
  open,
  onClose,
  date,
  athleteId,
  athleteName,
  onImportClick,
}) => {
  const [sessions, setSessions] = useState<ISession[]>([]);
  const [loading, setLoading] = useState(false);
  const [historySession, setHistorySession] = useState<ISession | null>(null);

  useEffect(() => {
    if (!open || !athleteId) {
      setSessions([]);
      return;
    }
    setLoading(true);
    axios
      .get<ISession[]>(`${baseUrl}/sessions/athlete/${athleteId}?limit=100`)
      .then((res) => {
        const filtered = res.data.filter(
          (s) => dayjs(s.date).format('YYYY-MM-DD') === date,
        );
        setSessions(filtered);
      })
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, [open, athleteId, date]);

  if (!open) return null;

  return (
    <>
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="blackAlpha.500"
      zIndex={1050}
      display="flex"
      alignItems="center"
      justifyContent="center"
      onClick={onClose}
    >
      <Box
        bg="var(--c-card-bg)"
        borderRadius="lg"
        p={6}
        maxWidth="640px"
        width="92%"
        onClick={(e) => e.stopPropagation()}
      >
        <HStack justifyContent="space-between" alignItems="flex-start" mb={4}>
          <Box>
            <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.05em">
              Dettaglio giorno
            </Text>
            <Text fontSize="2xl" fontWeight="700">
              {dayjs(date).format('dddd D MMMM YYYY')}
            </Text>
            {athleteName && (
              <Text fontSize="sm" color="gray.600">
                Atleta: <strong>{athleteName}</strong>
              </Text>
            )}
          </Box>
          <CustomButton color="text" onClick={onClose}>
            <X size={16} />
          </CustomButton>
        </HStack>

        {loading ? (
          <Box textAlign="center" py={6}><Spinner /></Box>
        ) : sessions.length === 0 ? (
          <Box bg="gray.50" p={6} borderRadius="md" textAlign="center">
            <Text color="gray.500" mb={3}>
              Nessuna sessione registrata per {athleteName ?? 'questo atleta'} il {date}
            </Text>
          </Box>
        ) : (
          <VStack alignItems="stretch" gap={2} mb={4}>
            {sessions.map((s) => (
              <HStack
                key={String(s._id)}
                p={3}
                bg="gray.50"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
              >
                <Box flex={1}>
                  <HStack gap={2}>
                    <Text fontWeight={600}>{s.name ?? s.sessionType}</Text>
                    {s.crossTeamImport && (
                      <ImportBadge originalFileTeam={s.originalFileTeam} />
                    )}
                  </HStack>
                  <Text fontSize="xs" color="gray.500">
                    {s.sport} · {s.duration ?? '?'}min · TD {s.commonMetrics?.td?.toLocaleString('it-IT') ?? '—'}m · RPE {s.internalLoad?.rpe ?? '—'}
                  </Text>
                </Box>
                <HStack gap={2}>
                  {(s.reassignmentHistory?.length ?? 0) > 0 && (
                    <CustomButton
                      color="text"
                      onClick={() => setHistorySession(s)}
                    >
                      <History size={14} />
                    </CustomButton>
                  )}
                  <Badge colorPalette={s.qualityClass === 'ok' ? 'green' : s.qualityClass === 'manual_entry' ? 'gray' : 'orange'}>
                    {s.qualityClass}
                  </Badge>
                </HStack>
              </HStack>
            ))}
          </VStack>
        )}

        <HStack justifyContent="flex-end" gap={2} mt={4}>
          <CustomButton color="text" onClick={onClose}>
            Chiudi
          </CustomButton>
          <CustomButton
            color="primary"
            onClick={() => { onImportClick(); onClose(); }}
          >
            {btnInline(<Download size={15} />, '+ Importa dati')}
          </CustomButton>
        </HStack>
      </Box>

    </Box>

    {/* Modale storico riassegnazioni — zIndex 1100, sopra il DayDetailModal */}
    {historySession && (
      <ReassignmentHistoryModal
        session={historySession}
        isOpen={true}
        onClose={() => setHistorySession(null)}
      />
    )}
    </>
  );
};
